import { CrownIcon, HeartIcon, TicketPercentIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import type { ClubSummary } from '@/modules/club/types/club';
import type { StoreReferralProgram } from '@/modules/store-referral/types';
import { describeReward } from '@/modules/store-referral/utils';

import type { StoreCoupon } from '../types/store-coupon';
import { couponShortLabel } from '../utils/store-benefits';

/** Quantos cupons aparecem como chip antes do "+N". */
const MAX_COUPON_CHIPS = 2;

type Tone = 'coupon' | 'club' | 'referral' | 'neutral';

const toneClass: Record<Tone, string> = {
  coupon: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  club: 'bg-violet-50 text-violet-700 ring-violet-200',
  referral: 'bg-primary/10 text-primary ring-primary/25',
  neutral: 'bg-gray-100 text-gray-700 ring-gray-200',
};

function BenefitChip({
  tone,
  icon,
  children,
  onClick,
  label,
}: {
  tone: Tone;
  icon?: ReactNode;
  children: ReactNode;
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset transition-transform active:scale-95 clickable-button',
        toneClass[tone]
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function clubChipLabel(club: ClubSummary): string {
  const balance = club.customer?.available_points;
  if (typeof balance === 'number' && balance > 0) {
    return `Clube · ${balance.toLocaleString('pt-BR')} pts`;
  }
  const perReal = club.program?.points_per_real;
  if (typeof perReal === 'number' && perReal > 0) {
    return `Clube · ${perReal.toLocaleString('pt-BR')} pt por R$ 1`;
  }
  return 'Clube · ganhe pontos';
}

interface StoreBenefitsStripProps {
  coupons: StoreCoupon[];
  club?: ClubSummary | null;
  referral?: StoreReferralProgram | null;
  onOpenCoupons: () => void;
  onOpenClub: () => void;
  onOpenReferral: () => void;
  className?: string;
}

/**
 * Faixa de benefícios da loja (PRAT-110): cupons, Clube (fidelidade) e
 * Afili'amados lado a lado, em chips roláveis. Some quando a loja não tem
 * nenhum benefício ativo.
 */
export function StoreBenefitsStrip({
  coupons,
  club,
  referral,
  onOpenCoupons,
  onOpenClub,
  onOpenReferral,
  className,
}: StoreBenefitsStripProps) {
  const hasClub = !!club?.enabled;
  const hasReferral = !!referral?.enabled;
  if (!coupons.length && !hasClub && !hasReferral) return null;

  const shown = coupons.slice(0, MAX_COUPON_CHIPS);
  const extra = coupons.length - shown.length;

  return (
    <section aria-label="Benefícios da loja" className={cn('flex flex-col gap-2', className)}>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Benefícios desta loja
      </p>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 hide-scrollbar sm:-mx-5 sm:px-5">
        {shown.map(coupon => (
          <BenefitChip
            key={coupon.id}
            tone="coupon"
            icon={<TicketPercentIcon className="size-3.5" aria-hidden />}
            onClick={onOpenCoupons}
            label={`Cupom ${couponShortLabel(coupon)}`}
          >
            {couponShortLabel(coupon)}
          </BenefitChip>
        ))}
        {extra > 0 && (
          <BenefitChip tone="neutral" onClick={onOpenCoupons} label={`Mais ${extra} cupons`}>
            +{extra}
          </BenefitChip>
        )}
        {hasClub && (
          <BenefitChip
            tone="club"
            icon={<CrownIcon className="size-3.5" aria-hidden />}
            onClick={onOpenClub}
          >
            {clubChipLabel(club!)}
          </BenefitChip>
        )}
        {hasReferral && (
          <BenefitChip
            tone="referral"
            icon={<HeartIcon className="size-3.5 fill-current" aria-hidden />}
            onClick={onOpenReferral}
          >
            Indique e ganhe {describeReward(referral!, 'referrer')}
          </BenefitChip>
        )}
      </div>
    </section>
  );
}
